import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowRight, CornerDownRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { getAvatarInitials } from "./utils";
import type { ChangeLogEntry } from "./types";
import { TruncatedText } from "@/components/ui/truncated-text";

interface ChangeHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading: boolean;
  entries: ChangeLogEntry[];
  // AUT-362: shared between KPS Configuration and Brand Intelligence, so the copy is per-page.
  description: string;
}

export function ChangeHistoryDialog({ open, onOpenChange, isLoading, entries, description }: ChangeHistoryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] bg-card">
        <DialogHeader>
          <DialogTitle>Configuration change history</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[480px] overflow-y-auto pr-1 -mx-1">
          <div className="space-y-2 px-1">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <span className="text-sm text-muted-foreground">Loading change history...</span>
              </div>
            )}
            {!isLoading && entries.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <span className="text-sm text-muted-foreground">No changes recorded yet.</span>
              </div>
            )}
            {!isLoading && entries.map((entry) => (
              <div key={entry.id} className="p-3 rounded-md border border-border hover:border-border-strong transition-colors">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-7 w-7 rounded-full bg-primary-tint-soft flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                      {getAvatarInitials(entry.user)}
                    </div>
                    <div className="min-w-0">
                      <TruncatedText className="text-sm font-semibold text-foreground">{entry.user}</TruncatedText>
                      <div className="text-xs text-muted-foreground">{entry.timestamp.replace(/(\d{4})\s+(\d)/, "$1 • $2")}</div>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-foreground mb-2">{entry.summary}</div>
                <div className="overflow-hidden">
                  {entry.edits.map((edit, i) => {
                    const prev = entry.edits[i - 1];
                    const isNested = edit.section !== "Scoring Weights";
                    const showHeader = isNested && prev?.section !== edit.section;
                    return (
                      <div key={i}>
                        {showHeader && (
                          <div className="flex items-center px-0 pt-1.5 pb-0.5">
                            <TruncatedText className="text-xs text-muted-foreground">{edit.section}</TruncatedText>
                          </div>
                        )}
                        <div className={cn("flex items-center justify-between gap-3 px-0 py-1", showHeader && "pt-0")}>
                          <span className="text-xs text-muted-foreground flex items-center gap-1.5 min-w-0">
                            {isNested && <CornerDownRight className="h-3 w-3 shrink-0 ml-2" />}
                            <TruncatedText>{edit.field}</TruncatedText>
                          </span>
                          <div className="flex items-center gap-2 text-xs shrink-0">
                            <span className="px-2 py-0.5 rounded-sm bg-status-error-soft text-foreground tabular-nums">{edit.oldValue}</span>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="px-2 py-0.5 rounded-sm bg-status-success-soft text-foreground tabular-nums font-semibold">{edit.newValue}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
